import { CheckCircle, Package } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

interface QuantityConfirmModalProps {
  visible: boolean
  barcode: string
  scanning: boolean
  onConfirm: (quantity: number) => void
  onCancel: () => void
}

const QuantityConfirmModal: React.FC<QuantityConfirmModalProps> = ({
  visible,
  barcode,
  scanning,
  onConfirm,
  onCancel
}) => {
  const [quantity, setQuantity] = useState<string>('1')
  const quantityInputRef = useRef<HTMLInputElement>(null)

  // 弹窗打开时聚焦到数量输入框
  useEffect(() => {
    if (visible && quantityInputRef.current) {
      quantityInputRef.current.focus()
      quantityInputRef.current.select()
    }
  }, [visible])

  // 重置数量为1当弹窗打开时
  useEffect(() => {
    if (visible) {
      setQuantity('1')
    }
  }, [visible])

  const handleConfirm = () => {
    const qty = parseInt(quantity)
    if (qty <= 0 || isNaN(qty)) {
      return
    }
    onConfirm(qty)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleConfirm()
    }
  }

  if (!visible) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* 弹窗标题 */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Package className="w-5 h-5 text-orange-500 mr-2" />
            确认批量扫描数量
          </h3>
        </div>

        {/* 弹窗内容 */}
        <div className="px-6 py-4 space-y-4">
          <div>
            <div className="text-sm text-gray-600 mb-2">扫描的Mark:</div>
            <div className="font-mono text-lg font-semibold text-gray-900 bg-gray-50 px-3 py-2 rounded-md break-all">
              {barcode}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              扫描数量:
            </label>
            <input
              ref={quantityInputRef}
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              onKeyDown={handleKeyDown}
              min="1"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              placeholder="请输入扫描数量"
            />
          </div>

          <div className="text-sm text-gray-500">
            📝 这将为该Mark批量添加 <strong>{quantity || '0'}</strong> 个扫描记录
          </div>
        </div>

        {/* 弹窗按钮 */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={onCancel}
            disabled={scanning}
            className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md transition-colors disabled:opacity-50"
          >
            取消
          </button>
          <button
            onClick={handleConfirm}
            disabled={scanning || !quantity || parseInt(quantity) <= 0}
            className={`px-4 py-2 rounded-md font-medium transition-colors flex items-center space-x-2 ${
              scanning
                ? 'bg-yellow-500 text-white'
                : 'bg-orange-600 hover:bg-orange-700 text-white disabled:bg-gray-300 disabled:text-gray-500'
            }`}
          >
            {scanning ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                <span>扫描中...</span>
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4" />
                <span>确认扫描</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default QuantityConfirmModal